import type {
  EndResponse,
  ErrorResponse,
  FilterResponse,
  InitialRequest as SearchInitialRequest,
  OfferResponse,
} from '@luxuryescapes/contract-search'
import config from 'constants/config'
import { unique } from 'lib/array/arrayUtils'
import {
  addSearchSocketListener,
  removeAllSearchSocketListenersAndSafelyDisconnect,
} from 'lib/search/searchSocketListeners'
import { clearLastMessage, emitSocketMessage } from 'lib/search/socketEmitter'
import uuidV4 from 'lib/string/uuidV4Utils'

export const EXPERIENCE_SEARCH_STREAM_TIMEOUT_MS = 20_000
export const EXPERIENCE_SEARCH_STREAM_PAGE_SIZE = 32
export const EXPERIENCE_SEARCH_PAGE_STREAM_LISTENER_ID = 'experience-search-page'

export function getExperienceSearchStreamEventGroupKey(listKey: string): string {
  return `experience-search-stream:${listKey}`
}

export interface ExperienceSearchStreamParams {
  placeId: string
  region: string
  categories?: Array<string>
  collections?: Array<string>
  luxPlusFeatures?: Array<App.LuxPlusFeatureFilter>
  sortBy?: Experiences.SortTypes
  from?: string
  to?: string
  priceGte?: number
  priceLte?: number
  minRating?: number
  campaigns?: Array<string>
  features?: Array<string>
  targetAudience?: Array<App.ExperienceTargetAudience>
  timeSlots?: Array<Experiences.TimeSlotFilter>
  bounds?: string
  /** Max offers to emit. Omit for count-only streams. */
  limit?: number
  /** First-batch size for Vision. Search page uses this instead of an unbounded list. */
  minOffersRequired?: number
  /** When true, request filters/total only (minOffersRequired: 0). */
  countOnly?: boolean
  searchId?: string
  /** Stable per list key on the search page so a new search can tear down the previous listeners. */
  eventGroupKey?: string
}

export interface ExperienceSearchStreamHandlers {
  onFilters?: (total: number | undefined) => void
  onOffer?: (experienceId: string) => void
  onEnd: () => void
  onError: (error: Error) => void
  onDisconnect?: () => void
  onConnect?: () => void
}

export function startExperienceSearchStream(
  params: ExperienceSearchStreamParams,
  handlers: ExperienceSearchStreamHandlers,
): { searchId: string, eventGroupKey: string, dispose: (error?: Error) => void } {
  if (typeof window === 'undefined') {
    throw new Error('Experience search streaming is browser-only')
  }

  const searchId = params.searchId ?? uuidV4()
  const eventGroupKey = params.eventGroupKey ?? `experience-search-stream:${searchId}`
  const countOnly = !!params.countOnly
  const minOffersRequired = countOnly ? 0 : (params.minOffersRequired ?? params.limit)
  const flexibleDates = params.from && params.to ? `${params.from}:${params.to}` : undefined
  let settled = false

  const settle = (error?: Error) => {
    if (settled) return
    settled = true
    clearLastMessage(searchId)
    removeAllSearchSocketListenersAndSafelyDisconnect({ eventGroupKey })
    if (error) {
      handlers.onError(error)
      return
    }
    handlers.onEnd()
  }

  addSearchSocketListener({
    eventGroupKey,
    event: 'search:response:filters',
    handler: (response: FilterResponse) => {
      if (response.searchId !== searchId) return
      handlers.onFilters?.(response.total)
    },
  })
  addSearchSocketListener({
    eventGroupKey,
    event: 'search:response:offer',
    handler: (response: OfferResponse) => {
      if (response.searchId !== searchId || !response.result?.id) return
      handlers.onOffer?.(response.result.id)
    },
  })
  addSearchSocketListener({
    eventGroupKey,
    event: 'search:response:end',
    handler: (response: EndResponse) => {
      if (response.searchId !== searchId) return
      settle()
    },
  })
  addSearchSocketListener({
    eventGroupKey,
    event: 'search:response:error',
    handler: (errorResponse: ErrorResponse) => {
      if ('searchId' in errorResponse && errorResponse.searchId && errorResponse.searchId !== searchId) return
      if (errorResponse.type === 'searchNotFound') {
        settle()
        return
      }
      settle(new Error(`Experience search stream failed: ${errorResponse.type}`))
    },
  })
  // Do not settle on socket disconnect. The shared search socket can drop
  // transiently; socketEmitter resends the stored initial request on reconnect.
  addSearchSocketListener({
    eventGroupKey,
    event: 'disconnect',
    handler: () => {
      handlers.onDisconnect?.()
    },
  })
  addSearchSocketListener({
    eventGroupKey,
    event: 'connect',
    handler: () => {
      handlers.onConnect?.()
    },
  })

  emitSocketMessage(
    searchId,
    'search:request:initial',
    {
      searchId,
      region: params.region as SearchInitialRequest['region'],
      brand: config.BRAND as SearchInitialRequest['brand'],
      placeIds: params.placeId,
      offerType: 'experience:*',
      occupancy: [],
      ...(params.limit != null ? { limit: params.limit } : {}),
      ...(minOffersRequired != null ? { minOffersRequired } : {}),
      ...(params.sortBy ? { sortBy: params.sortBy } : {}),
      ...(params.luxPlusFeatures?.length ? { luxPlusExclusive: params.luxPlusFeatures.join(',') } : {}),
      ...(params.categories?.length ? { categories: params.categories.join(',') } : {}),
      ...(params.collections?.length ? { collections: params.collections.join(',') } : {}),
      ...(params.campaigns?.length ? { campaigns: params.campaigns.join(',') } : {}),
      ...(params.features?.length ? { features: params.features.join(',') } : {}),
      ...(params.targetAudience?.length ? { targetAudience: params.targetAudience.join(',') } : {}),
      ...(params.timeSlots?.length ? { timeSlots: params.timeSlots.join(',') } : {}),
      ...(params.bounds ? { bounds: params.bounds } : {}),
      ...(flexibleDates ? { flexibleDates } : {}),
      ...(params.priceGte != null ? { priceGte: params.priceGte } : {}),
      ...(params.priceLte != null ? { priceLte: params.priceLte } : {}),
      ...(params.minRating != null ? { customerRatingGte: params.minRating } : {}),
    } as SearchInitialRequest,
  )

  return { searchId, eventGroupKey, dispose: settle }
}

export function extendExperienceSearchStream(searchId: string, minOffers: number): void {
  emitSocketMessage(
    searchId,
    'search:request:extend-to',
    { searchId, minOffers },
  )
}

export function getExperienceSearchStreamList(
  params: ExperienceSearchStreamParams,
): Promise<{ experienceIds: Array<string>, total?: number }> {
  if (typeof window === 'undefined') {
    return Promise.reject(new Error('Experience search streaming is browser-only'))
  }

  const experienceIds: Array<string> = []
  let total: number | undefined

  return new Promise((resolve, reject) => {
    const timeout = { id: undefined as ReturnType<typeof setTimeout> | undefined }
    const session = startExperienceSearchStream(params, {
      onFilters: (nextTotal) => {
        total = nextTotal
      },
      onOffer: (experienceId) => {
        experienceIds.push(experienceId)
      },
      onEnd: () => {
        if (timeout.id) clearTimeout(timeout.id)
        resolve({
          experienceIds: unique(experienceIds),
          ...(total != null ? { total } : {}),
        })
      },
      onError: (error) => {
        if (timeout.id) clearTimeout(timeout.id)
        reject(error)
      },
    })

    // Homepage carousels wait for end; keep the original bounded-list timeout there.
    timeout.id = setTimeout(() => {
      session.dispose(new Error('Experience search stream timed out'))
    }, EXPERIENCE_SEARCH_STREAM_TIMEOUT_MS)
  })
}
